import { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Users, Wallet, Download, UserCog, LogOut, Menu, X, Newspaper, CalendarDays, Megaphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const isSuper = user?.role === "super_admin";
  const isMedia = user?.role === "media_admin";

  const links = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, show: !isMedia },
    { to: "/admin/members", label: "Member Directory", icon: Users, show: !isMedia },
    { to: "/admin/feed", label: "Community Feed", icon: Newspaper, show: !isMedia },
    { to: "/admin/events", label: "Events", icon: CalendarDays, show: !isMedia },
    { to: "/admin/press", label: "Media & Press", icon: Megaphone, show: isSuper || isMedia },
    { to: "/admin/rewards", label: "Reward Management", icon: Wallet, show: isSuper },
    { to: "/admin/export", label: "Data Export", icon: Download, show: !isMedia },
    { to: "/admin/users", label: "User Management", icon: UserCog, show: isSuper },
  ].filter((l) => l.show);

  const doLogout = () => { logout(); nav("/admin/login"); };

  const Sidebar = () => (
    <div className="flex flex-col h-full">
      <Link to="/" className="flex items-center gap-3 px-6 h-20 border-b border-slate-800">
        <img src={LOGO_URL} alt="LBM" className="h-10 w-10 rounded-full object-contain bg-white ring-2 ring-secondary p-0.5" />
        <div className="leading-tight"><div className="font-heading font-extrabold text-white text-sm">London Boy</div><div className="text-[10px] text-secondary tracking-wide">CONTROL CENTER</div></div>
      </Link>
      <nav className="flex-1 p-4 space-y-1">
        {links.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)} data-testid={`admin-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-primary text-white" : "text-slate-300 hover:bg-slate-800"}`}>
            <l.icon className="w-5 h-5" /> {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-slate-800">
        <div className="px-4 py-2 mb-2">
          <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
          <div className="text-xs text-secondary">{isSuper ? "Super Admin" : isMedia ? "Media & Press Admin" : `LGA Admin · ${user?.assigned_lga}`}</div>
        </div>
        <Button data-testid="admin-logout" onClick={doLogout} variant="ghost" className="w-full justify-start gap-3 text-slate-300 hover:bg-slate-800 hover:text-white rounded-xl"><LogOut className="w-5 h-5" /> Logout</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden lg:block w-64 bg-slate-900 fixed inset-y-0"><Sidebar /></aside>
      {open && <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setOpen(false)} />}
      <aside className={`lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 transition-transform ${open ? "translate-x-0" : "-translate-x-full"}`}><Sidebar /></aside>

      <div className="flex-1 lg:ml-64">
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b h-16 flex items-center justify-between px-4">
          <button data-testid="admin-mobile-toggle" onClick={() => setOpen(!open)} className="p-2 text-primary">{open ? <X /> : <Menu />}</button>
          <span className="font-heading font-bold text-primary">Control Center</span>
          <span className="w-8" />
        </header>
        <div className="p-5 md:p-8"><Outlet /></div>
      </div>
    </div>
  );
}
